/**
 * 충전금 > 충전 (/m/account/deposit)
 *
 * 충전은 신청이 없다 — 내 충전계좌(가상계좌)로 보내면 서버가 알아서 채운다. 그래서 이 화면의 일은
 * '들어왔는지 확인'이다: [새로고침]을 누를 때와 은행 앱에서 돌아올 때(visibilitychange) 같은 조회를
 * 불러 잔액·내역을 새로고침 없이 다시 그리고, 새로 들어온 충전은 토스트와 줄 강조로 알린다.
 */
import Util from '../../Common/Util';
import CashBalance from './Cash/Balance';

const PAGE_SIZE = 10;
// 충전 안내를 접어 뒀는지 ('1' = 접음). 기기 취향이라 회원번호를 붙이지 않는다 (.ai/state-management.md §4-1)
const LS_NOTICE_FOLDED = 'm:cash:depositNoticeFolded';
const AUTO_REFRESH_GAP = 5000;  // 앱 복귀 자동 조회 최소 간격(ms) — 화면이 깜빡일 때마다 부르지 않게

// g5_cash_request.status (충전) → [뱃지 클래스, 문구]
const STATUS = {
    10: ['is-wait', '충전대기'],
    11: ['is-done', '충전완료'],
    12: ['is-cancel', '충전취소'],
};

export default class AccountDeposit {
    initialize() {
        this.$root = $('#cdRoot');
        if (!this.$root.length) return;

        const d = this.$root.data();
        // 잔액 카드 + 상세 시트 — 사용내역 화면과 같이 쓰는 모듈
        this.card = new CashBalance({
            $usable: $('#cdUsable'), $rows: $('#cdRows'),
            balance: { total: d.total, soo_hold: d.sooHold, request: d.request, bal_hold: d.balHold },
        });
        this.isPenalty = d.penalty == 1;

        this.rows = [];
        this.monthSums = {};
        this.recordsTotal = 0;
        this.newNums = [];          // 방금 새로고침에서 새로 들어온 줄 — 한 번만 강조한다
        this.refreshedAt = 0;
        this.loadingMore = false;

        this.$hist = $('#cdHist');
        this.$more = $('#cdMore');
        this.$refresh = $('#cdRefresh');

        this.$more.on('click', () => this.loadMore());
        this.$refresh.on('click', () => this.refresh('button'));

        // 무한 스크롤 — '더 보기' 자리가 화면에 들어오면 다음 쪽을 자동으로 붙인다.
        // attach 가 null 이면(IntersectionObserver 미지원) 버튼이 그대로 폴백으로 남는다.
        if ($j.Ui.InfiniteScroll.attach(this.$more[0], { onLoad: () => this.loadMore() })) {
            this.$more.addClass('is-auto');
        }
        $('#cdIssue').on('click', () => this.onClickIssue());
        $('#cdRetry').on('click', () => this.onClickRetry());
        $('#cdCopy').on('click', () => this.onClickCopy());

        // 충전 안내 — 접은 적이 없으면 펼쳐서 시작한다
        this._setNotice($j.Common.Store.local.get(LS_NOTICE_FOLDED) !== '1');
        $('#cdNoticeBtn').on('click', () => this.onClickNotice());

        // 은행 앱에서 보내고 돌아오면 바로 확인한다
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible' && Date.now() - this.refreshedAt > AUTO_REFRESH_GAP) this.refresh('return');
        });

        this.card.render();
        this.refresh('enter');
    }

    won(n) {
        return Util.formatNumber(n) + '원';
    }

    /* ───────────────── 잔액 ───────────────── */

    /** 기준 시각 — 지금 보는 숫자가 언제 것인지 */
    _stampRefreshed() {
        this.refreshedAt = Date.now();
        $('#cdRefAt').text(dayjs().format('HH:mm') + ' 기준');
    }

    /* ───────────────── 새로고침 · 내역 ───────────────── */

    _fetch(start, length, withBalance) {
        return $.post('/m/ajax/account/list', {
            type: 1, start, length, month_sum: 1, balance: withBalance ? 1 : '',
        });
    }

    /**
     * 잔액과 내역을 처음부터 다시 받는다.
     * @param {'enter'|'button'|'return'} why 들어올 때 · 버튼 · 앱 복귀. 버튼일 때만 '새 입금 없음'을 알린다
     */
    refresh(why) {
        if (this.$refresh.attr('aria-busy') === 'true') return;
        this.$refresh.attr('aria-busy', 'true');

        // 이미 더 불러온 만큼은 유지한다 — 새로고침했다고 펼쳐 둔 목록이 접히지 않게
        const length = Math.max(PAGE_SIZE, this.rows.length);
        const known = this.rows.map((r) => String(r.num));

        this._fetch(0, length, true)
            .done((res) => {
                const rows = res.data || [];
                // 들어올 때는 비교할 '이전'이 없다 — 강조·토스트는 새로고침부터
                const fresh = why === 'enter' ? [] : rows.filter((r) => !known.includes(String(r.num)));
                this.newNums = fresh.map((r) => String(r.num));

                this.rows = rows;
                this.monthSums = res.monthSums || {};
                this.recordsTotal = Util.int(res.recordsTotal);
                if (res.balance) this.card.set(res.balance);

                this._stampRefreshed();
                this.card.render();
                this.renderHistory();
                this.newNums = [];

                const charged = fresh.filter((r) => r.status == 11).reduce((sum, r) => sum + Util.int(r.amount), 0);
                if (charged > 0) this._toast('충전됐어요');
                else if (why === 'button') this._toast('새 충전금이 없어요');
            })
            .fail(() => { if (why === 'button') this._toast('불러오지 못했어요. 잠시 후 다시 눌러 주세요.', 'error'); })
            .always(() => this.$refresh.removeAttr('aria-busy'));
    }

    loadMore() {
        // 무한 스크롤의 onLoad 는 연달아 불릴 수 있다 — 로딩 중 재진입을 막는다.
        // 새로고침(refresh) 중에도 멈춘다 — 그쪽이 목록을 통째로 다시 받는 중이라 겹쳐 받으면 어긋난다.
        if (this.loadingMore || this.$refresh.attr('aria-busy') === 'true') return;
        this.loadingMore = true;
        this.$more.prop('disabled', true);
        this._fetch(this.rows.length, PAGE_SIZE, false)
            .done((res) => {
                this.rows = this.rows.concat(res.data || []);
                this.monthSums = res.monthSums || {};
                this.recordsTotal = Util.int(res.recordsTotal);
                this.renderHistory();
            })
            .always(() => { this.loadingMore = false; this.$more.prop('disabled', false); });
    }

    renderHistory() {
        this.$more.prop('hidden', this.rows.length >= this.recordsTotal);

        if (!this.rows.length) {
            this.$hist.html(
                '<div class="cx-card cx-empty">'
                + '<svg class="cx-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3.5" width="16" height="17" rx="2.5"/><path d="M8 8.5h8M8 12.5h8M8 16.5h5"/></svg>'
                + '<p class="cx-empty-tt">아직 충전 내역이 없어요</p>'
                + '<p class="cx-empty-desc">내 충전계좌로 입금하면 여기에 표시돼요.</p></div>'
            );
            return;
        }

        this.$hist.html(CashBalance.groupByMonth(this.rows, 'request_date').map((g) => {
            const sum = Util.formatNumber(Util.int(this.monthSums[g.ym]));
            return `<div class="cx-card cx-hist">`
                + `<h3 class="cx-month">${dayjs(g.ym + '-01').format('YYYY년 M월')}`
                + `<span class="cx-month-sum"><i>합계</i> ${sum}원</span></h3>`
                + g.rows.map((row) => this._rowHtml(row)).join('')
                + `</div>`;
        }).join(''));
    }

    _rowHtml(row) {
        const [badgeClass, badgeText] = STATUS[row.status] || ['', Util.nvl(row.status, '')];
        const when = dayjs(row.request_date);
        const who = this._esc(Util.nvl(row.request_username, ''));
        const cls = (row.status == 12 ? ' is-cancel' : '') + (this.newNums.includes(String(row.num)) ? ' is-new' : '');

        return `<div class="cx-row${cls}">`
            + `<div class="cx-row-mid"><span class="cx-when">${when.format('M.D')} <em>${when.format('HH:mm')}</em></span>`
            + (who ? `<span class="cx-who-line"><i class="cd-who-k">입금자</i> ${who}</span>` : '') + `</div>`
            + `<div class="cx-row-right"><span class="cx-amt">+${this.won(Util.int(row.amount))}</span>`
            + `<span class="cx-badge ${badgeClass}">${badgeText}</span></div></div>`;
    }

    _esc(str) {
        return $('<i>').text(str).html();
    }

    _toast(msg, type = 'success') {
        $j.Ui.Toast.show(msg, { type, place: 'bottom' });
    }

    /* ───────────────── 내 충전계좌 ───────────────── */

    onClickIssue() {
        const $btn = $('#cdIssue');
        if ($btn.attr('aria-busy') === 'true') return;
        if (this.isPenalty) { $j.Ui.Dialog.alert('환원건이 있어 입출금을 할 수 없어요.'); return; }

        const label = $btn.text();
        $btn.attr('aria-busy', 'true').html('<span class="cd-spin" aria-hidden="true"></span>발급하고 있어요');

        $.post('/m/ajax/account/requestVirtualAccount')
            .done((res) => {
                if (!res.result) { this._showIssueFail(res.message); return; }
                const no = String(res.account.account);
                $('#cdAcctNo').text([no.slice(0, 4), no.slice(4, 8), no.slice(8)].join('-'));
                $('#cdAcctNone').prop('hidden', true);
                $('#cdAcctIssued, #cdAcctGuide').prop('hidden', false);
                this._toast('충전계좌가 발급됐어요');
            })
            .fail(() => this._showIssueFail(''))
            .always(() => $btn.removeAttr('aria-busy').text(label));
    }

    _setNotice(open) {
        $('#cdNoticeBtn').attr('aria-expanded', open ? 'true' : 'false');
        $('#cdNoticeBd').prop('hidden', !open);
    }

    /** 접으면 기억하고, 다시 펴면 기억을 지운다 — 마지막에 둔 상태가 다음 방문의 시작 상태다 */
    onClickNotice() {
        const open = $('#cdNoticeBtn').attr('aria-expanded') !== 'true';
        this._setNotice(open);
        if (open) $j.Common.Store.local.remove(LS_NOTICE_FOLDED);
        else $j.Common.Store.local.set(LS_NOTICE_FOLDED, '1');
    }

    /** 실패 카드 — [다시 시도]와 고객센터 연결을 같이 둔다 (통신 오류는 다시 누르면 되고, 막힌 계좌는 고객센터가 푼다) */
    _showIssueFail(msg) {
        $('#cdFailMsg').text(msg || '').prop('hidden', !msg);
        $('#cdAcctNone').prop('hidden', true);
        $('#cdAcctFail').prop('hidden', false);
    }

    /** 발급 전 카드로 되돌려 같은 발급을 다시 부른다 — 진행 표시는 [계좌 발급받기] 버튼이 한다 */
    onClickRetry() {
        $('#cdAcctFail').prop('hidden', true);
        $('#cdAcctNone').prop('hidden', false);
        this.onClickIssue();
    }

    /** 계좌번호 숫자만 복사한다 — 은행 앱 계좌번호 칸에 그대로 붙여넣게 (은행명·하이픈 빼고) */
    onClickCopy() {
        const text = $('#cdAcctNo').text().replace(/\D/g, '');
        const done = () => this._toast('계좌번호를 복사했어요');
        // 웹뷰·http 에서는 navigator.clipboard 가 없거나 거절된다 — 그때는 임시 입력칸으로 복사한다
        const legacy = () => {
            const $t = $('<textarea readonly>').val(text).css({ position: 'fixed', top: 0, left: '-9999px' }).appendTo('body');
            $t[0].select();
            $t[0].setSelectionRange(0, text.length);
            let ok = false;
            try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
            $t.remove();
            if (ok) done(); else this._toast('복사하지 못했어요. 계좌번호를 길게 눌러 복사해 주세요.', 'error');
        };

        if (navigator.clipboard && window.isSecureContext) navigator.clipboard.writeText(text).then(done, legacy);
        else legacy();
    }
}
